import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Check, CornerDownRight, EyeOff, Flag, Heart, Lock, LogIn, MessageCircle, MoreHorizontal, Pencil, Send, ShieldCheck, Trash2,
} from 'lucide-react';
import { adminService, apiErrorMessage, articleService } from '../../services/api';
import { Button, Modal } from '../ui';
import { relativeTime } from '../../utils/articles';
import { initials } from '../../utils/share';
import './Reader.css';

export const REASONS = [
  { value: 'spam', label: 'Spam' },
  { value: 'ofensivo', label: 'Ofensivo' },
  { value: 'fora_do_tema', label: 'Fora do tema' },
  { value: 'outro', label: 'Outro' },
];
const reasonLabel = (v) => REASONS.find(r => r.value === v)?.label.toLowerCase() || v;

const PAGE = 5;

/**
 * Comentários do artigo para quem está logado: escrever, responder (1 nível), curtir,
 * editar e excluir os próprios, denunciar e, para o admin, a moderação.
 */
export function Comments({ article, user, onTotal, anchorRef }) {
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const canWrite = !user?.demo;
  const [data, setData] = useState(null);
  const [sort, setSort] = useState('relevant');
  const [all, setAll] = useState(false);
  const [reportedOnly, setReportedOnly] = useState(false);
  const [error, setError] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [reporting, setReporting] = useState(null);

  const load = useCallback(() => {
    articleService.comments(article.id, { sort, limit: all ? 0 : PAGE, reported: reportedOnly })
      .then(d => { setData(d); onTotal?.(d.total); })
      .catch(() => setError('Não foi possível carregar os comentários.'));
  }, [article.id, sort, all, reportedOnly, onTotal]);

  useEffect(load, [load]);

  const run = async (fn, fallback) => {
    setError(null);
    try {
      await fn();
      load();
    } catch (err) {
      setError(apiErrorMessage(err, fallback));
    }
  };

  const handlers = {
    reply: (body, parentId) => run(() => articleService.comment(article.id, body, parentId), 'Não foi possível responder.'),
    edit: (id, body) => run(() => articleService.editComment(id, body), 'Não foi possível editar.'),
    remove: (id) => run(() => articleService.deleteComment(id), 'Não foi possível excluir.'),
    like: (id) => articleService.likeComment(id),
    report: (c) => setReporting(c),
    moderate: (c) => setRemoving(c),
    restore: (id) => run(() => adminService.restoreComment(id), 'Não foi possível desfazer.'),
  };

  const total = data?.total ?? 0;
  return (
    <section className="rd-card rd-comments" ref={anchorRef} aria-label="Comentários" data-tour="artigo-comentarios">
      <div className="rd-comments__head">
        <h2 className="rd-comments__title">
          <MessageCircle size={15} aria-hidden="true" />Comentários<span>{total}</span>
        </h2>
        {isAdmin && data?.reportedCount != null && (
          <button
            type="button"
            className={`rd-mod ${reportedOnly ? 'is-active' : ''}`}
            data-tour="moderacao"
            aria-pressed={reportedOnly}
            title={reportedOnly ? 'Mostrar todos os comentários' : 'Você é admin: mostrar só os denunciados'}
            onClick={() => setReportedOnly(v => !v)}
          >
            {reportedOnly ? <Check size={12} aria-hidden="true" /> : <ShieldCheck size={12} aria-hidden="true" />}
            Moderação · {data.reportedCount} {data.reportedCount === 1 ? 'denunciado' : 'denunciados'}
          </button>
        )}
        <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Ordenar comentários">
          <option value="relevant">Mais relevantes</option>
          <option value="recent">Mais recentes</option>
        </select>
      </div>

      {canWrite ? (
        <CommentBox user={user} onSubmit={(body) => handlers.reply(body, null)} />
      ) : (
        <p className="rd-comments__demo">Comentários e curtidas ficam desligados na demonstração.</p>
      )}

      {error && <p className="rd-comments__error" role="alert">{error}</p>}
      {data && data.items.length === 0 && (
        <p className="rd-comments__empty">{reportedOnly ? 'Nenhum comentário denunciado.' : 'Ninguém comentou ainda. Que tal começar?'}</p>
      )}
      {data?.items.length > 0 && (
        <div className="rd-comments__list">
          {data.items.map(c => (
            <div key={c.id} className="rd-thread">
              <CommentItem comment={c} article={article} user={user} isAdmin={isAdmin} canWrite={canWrite} handlers={handlers} />
              {c.replies.map(r => (
                <CommentItem key={r.id} comment={r} article={article} user={user} isAdmin={isAdmin} canWrite={canWrite}
                             handlers={handlers} reply parentId={c.id} />
              ))}
            </div>
          ))}
        </div>
      )}
      {data?.remaining > 0 && (
        <button type="button" className="rd-comments__more" onClick={() => setAll(true)}>
          Ver mais {data.remaining} {data.remaining === 1 ? 'comentário' : 'comentários'}
        </button>
      )}

      {removing && (
        <RemoveModal
          comment={removing}
          onClose={() => setRemoving(null)}
          onConfirm={(reason, notify) => run(async () => {
            await adminService.removeComment(removing.id, reason, notify);
            setRemoving(null);
          }, 'Não foi possível remover.')}
        />
      )}
      {reporting && (
        <ReportModal
          comment={reporting}
          onClose={() => setReporting(null)}
          onConfirm={(reason) => run(async () => {
            await articleService.reportComment(reporting.id, reason);
            setReporting(null);
          }, 'Não foi possível denunciar.')}
        />
      )}
    </section>
  );
}

function CommentBox({ user, onSubmit, placeholder = 'Escreva um comentário...', autoFocus = false, onCancel, compact = false }) {
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    await onSubmit(body.trim());
    setSending(false);
    setBody('');
  };
  return (
    <form className={`rd-box ${compact ? 'rd-box--compact' : ''}`} onSubmit={submit}>
      <span className={`rd-avatar ${compact ? 'rd-avatar--sm' : ''}`}>{initials(user?.name)}</span>
      <div className="rd-box__main">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={placeholder}
          rows={2}
          maxLength={4000}
          autoFocus={autoFocus}
          aria-label="Comentário"
        />
        <div className="rd-box__foot">
          <span>Seja respeitoso. Comentários podem ser removidos pela moderação.</span>
          <span className="rd-box__actions">
            {onCancel && <Button variant="ghost" size="sm" onClick={onCancel}>Cancelar</Button>}
            <Button type="submit" size="sm" icon={Send} disabled={sending || !body.trim()}>{compact ? 'Responder' : 'Comentar'}</Button>
          </span>
        </div>
      </div>
    </form>
  );
}

function CommentItem({ comment: c, user, isAdmin, canWrite, handlers, reply = false, parentId }) {
  const [liked, setLiked] = useState(c.liked);
  const [likes, setLikes] = useState(c.likes);
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(c.body || '');
  const [replying, setReplying] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => { setLiked(c.liked); setLikes(c.likes); }, [c.liked, c.likes]);

  useEffect(() => {
    if (!menu) return undefined;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menu]);

  const toggleLike = async () => {
    if (!canWrite) return;
    setLiked(!liked);
    setLikes(n => n + (liked ? -1 : 1));
    try {
      const r = await handlers.like(c.id);
      setLiked(r.liked);
      setLikes(r.likes);
    } catch {
      setLiked(liked);
      setLikes(c.likes);
    }
  };

  const reported = isAdmin && c.reports > 0 && !c.removed;

  if (c.removed) {
    const { reason, byMe } = c.removed;
    return (
      <div className={`rd-comment rd-comment--gone ${reply ? 'rd-comment--reply' : ''}`}>
        <span className={`rd-avatar rd-avatar--ghost ${reply ? 'rd-avatar--sm' : ''}`}><EyeOff size={14} aria-hidden="true" /></span>
        <div className="rd-comment__main">
          <p className="rd-comment__gone">
            {c.mine && !isAdmin ? 'Seu comentário foi removido pela moderação.' : 'Comentário removido pela moderação.'}
          </p>
          {(isAdmin || reason) && (
            <span className="rd-comment__modline">
              {isAdmin ? (byMe ? 'Removido por você' : 'Removido pela moderação') : 'Motivo'}
              {reason && ` · motivo: ${reasonLabel(reason)}`}
              {isAdmin && <>{' · '}<button type="button" onClick={() => handlers.restore(c.id)}>Desfazer</button></>}
            </span>
          )}
        </div>
      </div>
    );
  }

  if (c.deleted) {
    return (
      <div className={`rd-comment rd-comment--gone ${reply ? 'rd-comment--reply' : ''}`}>
        <span className={`rd-avatar rd-avatar--ghost ${reply ? 'rd-avatar--sm' : ''}`}><Trash2 size={14} aria-hidden="true" /></span>
        <div className="rd-comment__main"><p className="rd-comment__gone">Comentário excluído pelo autor.</p></div>
      </div>
    );
  }

  return (
    <div className={`rd-comment ${reply ? 'rd-comment--reply' : ''} ${reported ? 'is-reported' : ''}`}>
      <span className={`rd-avatar ${reply ? 'rd-avatar--sm rd-avatar--reply' : ''}`}>{c.author.initials}</span>
      <div className="rd-comment__main">
        <div className="rd-comment__head">
          <strong>{c.author.name}</strong>
          {c.isArticleAuthor && <span className="rd-comment__badge">Autor</span>}
          {reported && <span className="rd-comment__badge rd-comment__badge--warn"><Flag size={11} aria-hidden="true" />{c.reports} {c.reports === 1 ? 'denúncia' : 'denúncias'}</span>}
          <span className="rd-comment__time">· {relativeTime(c.createdAt)}{c.editedAt ? ' · editado' : ''}</span>
          <span className="rd-comment__tools">
            {isAdmin && !c.mine && (
              <button type="button" className="rd-comment__remove" onClick={() => handlers.moderate(c)}>
                <Trash2 size={14} aria-hidden="true" />Remover
              </button>
            )}
            {canWrite && (
              <span className="rd-menu" ref={menuRef}>
                <button type="button" className="rd-menu__btn" onClick={() => setMenu(m => !m)} aria-label="Mais ações" aria-expanded={menu}>
                  <MoreHorizontal size={16} aria-hidden="true" />
                </button>
                {menu && (
                  <span className="rd-menu__pop" role="menu">
                    {c.mine ? (
                      <>
                        <button type="button" role="menuitem" onClick={() => { setEditing(true); setMenu(false); }}><Pencil size={14} aria-hidden="true" />Editar</button>
                        <button type="button" role="menuitem" className="is-danger" onClick={() => { setMenu(false); handlers.remove(c.id); }}><Trash2 size={14} aria-hidden="true" />Excluir</button>
                      </>
                    ) : (
                      <button type="button" role="menuitem" disabled={c.reportedByMe} onClick={() => { setMenu(false); handlers.report(c); }}>
                        <Flag size={14} aria-hidden="true" />{c.reportedByMe ? 'Denunciado' : 'Denunciar'}
                      </button>
                    )}
                  </span>
                )}
              </span>
            )}
          </span>
        </div>
        {editing ? (
          <form className="rd-edit" onSubmit={(e) => { e.preventDefault(); handlers.edit(c.id, text); setEditing(false); }}>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} aria-label="Editar comentário" autoFocus />
            <span>
              <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setText(c.body); }}>Cancelar</Button>
              <Button type="submit" size="sm" disabled={!text.trim()}>Salvar</Button>
            </span>
          </form>
        ) : (
          <p className="rd-comment__body">{c.body}</p>
        )}
        <div className="rd-comment__actions">
          <button type="button" className={`rd-like ${liked ? 'is-liked' : ''}`} onClick={toggleLike} disabled={!canWrite} aria-pressed={liked} aria-label="Curtir comentário">
            <Heart size={14} fill={liked ? 'currentColor' : 'none'} aria-hidden="true" />{likes}
          </button>
          {canWrite && (
            <button type="button" className="rd-reply" onClick={() => setReplying(r => !r)}>
              <CornerDownRight size={14} aria-hidden="true" />Responder
            </button>
          )}
        </div>
        {replying && (
          <CommentBox
            compact
            autoFocus
            user={user}
            placeholder={`Responder ${c.author.name.split(' ')[0]}...`}
            onCancel={() => setReplying(false)}
            onSubmit={async (body) => { await handlers.reply(body, parentId || c.id); setReplying(false); }}
          />
        )}
      </div>
    </div>
  );
}

/** Moderação: remover com motivo obrigatório e aviso opcional ao autor. */
function RemoveModal({ comment, onClose, onConfirm }) {
  const [reason, setReason] = useState(null);
  const [notify, setNotify] = useState(true);
  return (
    <Modal
      open
      onClose={onClose}
      title="Remover comentário"
      width={460}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button variant="danger" icon={Trash2} disabled={!reason} onClick={() => onConfirm(reason, notify)}>Remover</Button>
        </>
      )}
    >
      <blockquote className="rd-quote"><strong>{comment.author.name}:</strong> {comment.body}</blockquote>
      <ReasonChips label="Motivo" value={reason} onChange={setReason} />
      <label className="rd-check">
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
        Avisar o autor do comentário
      </label>
      <p className="rd-modal__note">
        No lugar do comentário fica "Comentário removido pela moderação". As respostas continuam visíveis.
      </p>
    </Modal>
  );
}

function ReportModal({ comment, onClose, onConfirm }) {
  const [reason, setReason] = useState(null);
  return (
    <Modal
      open
      onClose={onClose}
      title="Denunciar comentário"
      width={460}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button icon={Flag} disabled={!reason} onClick={() => onConfirm(reason)}>Denunciar</Button>
        </>
      )}
    >
      <blockquote className="rd-quote"><strong>{comment.author.name}:</strong> {comment.body}</blockquote>
      <ReasonChips label="O que há de errado?" value={reason} onChange={setReason} />
      <p className="rd-modal__note">A moderação revisa a denúncia. Quem escreveu não sabe quem denunciou.</p>
    </Modal>
  );
}

function ReasonChips({ label, value, onChange }) {
  return (
    <div className="rd-reasons">
      <span className="rd-reasons__label">{label} <span className="rd-req">*</span></span>
      <div role="radiogroup" aria-label={label}>
        {REASONS.map(r => (
          <button key={r.value} type="button" role="radio" aria-checked={value === r.value}
                  className={`rd-reason ${value === r.value ? 'is-active' : ''}`} onClick={() => onChange(r.value)}>
            {value === r.value && <Check size={13} aria-hidden="true" />}{r.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Comentários só leitura (artigo público, sem login). */
/**
 * Comentários para o visitante, num card só: contagens, a faixa de login e os primeiros comentários.
 * @param onRequest abre o pedido de acesso
 */
export function ReadOnlyComments({ data, likes, onLogin, onRequest }) {
  const total = data?.total ?? 0;
  const shown = (data?.items || []).reduce((n, c) => n + 1 + c.replies.length, 0);
  return (
    <section className="rd-card rd-comments" aria-label="Comentários">
      <div className="rd-comments__head">
        <h2 className="rd-comments__title">
          <MessageCircle size={15} aria-hidden="true" />Comentários<span>{total}</span>
        </h2>
        <span className="rd-comments__likes"><Heart size={12} aria-hidden="true" />{likes} {likes === 1 ? 'curtida' : 'curtidas'}</span>
      </div>
      <div className="rd-gate">
        <span className="rd-gate__icon"><Lock size={16} aria-hidden="true" /></span>
        <span className="rd-gate__text">
          <strong>Entre para curtir, comentar e salvar</strong>
          <span>
            O StudySpace é usado por turmas de pós-graduação. Ainda não tem acesso?{' '}
            <button type="button" onClick={onRequest}>Peça ao administrador</button>.
          </span>
        </span>
        <Button variant="accent" size="sm" icon={LogIn} onClick={onLogin}>Entrar</Button>
      </div>
      {data?.items.length === 0 && <p className="rd-comments__empty rd-comments__empty--flat">Ninguém comentou ainda.</p>}
      {data?.items.length > 0 && (
        <div className="rd-comments__list">
          {data.items.map(c => (
            <div key={c.id} className="rd-thread">
              {[c, ...c.replies].map((x, i) => (
                <div key={x.id} className={`rd-comment ${i > 0 ? 'rd-comment--reply' : ''}`}>
                  <span className={`rd-avatar rd-avatar--sm ${i > 0 ? 'rd-avatar--reply' : ''}`}>{x.removed || x.deleted ? <EyeOff size={14} aria-hidden="true" /> : x.author.initials}</span>
                  <div className="rd-comment__main">
                    {x.removed || x.deleted ? (
                      <p className="rd-comment__gone">{x.removed ? 'Comentário removido pela moderação.' : 'Comentário excluído pelo autor.'}</p>
                    ) : (
                      <>
                        <div className="rd-comment__head">
                          <strong>{x.author.name}</strong>
                          {x.isArticleAuthor && <span className="rd-comment__badge">Autor</span>}
                          <span className="rd-comment__time">· {relativeTime(x.createdAt)}</span>
                        </div>
                        <p className="rd-comment__body">{x.body}</p>
                        <div className="rd-comment__actions">
                          <span className="rd-like rd-like--static"><Heart size={14} aria-hidden="true" />{x.likes}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      {total > shown && (
        <button type="button" className="rd-comments__more" onClick={onLogin}>Entre para ver todos os comentários</button>
      )}
    </section>
  );
}
